/**
 * ClientRenewals — Dashboard des reconductions clients.
 *
 * Trois onglets :
 *   1. À confirmer  : contrats en état `reconduction_a_confirmer` → boutons Confirmer/Rejeter
 *   2. En reconduction : contrats reconduits actifs (audit historique)
 *   3. Échéances     : contrats dont end_date approche (horizon 90j)
 *
 * Bouton "Scan auto-proposal" en haut → propose les reconductions pour tous
 * les contrats tacit_renewal=true dont l'échéance approche.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  RefreshCw, CheckCircle2, XCircle, Zap, ArrowUpRight, Clock,
} from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

export default function ClientRenewals({ embedded = false } = {}) {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("contracts.write");
  const [toConfirm, setToConfirm] = useState([]);
  const [inRenewal, setInRenewal] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rC, rI, rU] = await Promise.all([
        api.get("/client-renewals?state=reconduction_a_confirmer"),
        api.get("/client-renewals?state=en_reconduction"),
        api.get("/client-renewals?horizon_days=90"),
      ]);
      setToConfirm(rC.data.contracts || []);
      setInRenewal(rI.data.contracts || []);
      setUpcoming(rU.data.contracts || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/client-renewals/auto-propose");
      toast.success(`${r.data.proposed} proposition(s) créée(s) sur ${r.data.scanned} contrat(s) tacites`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setScanning(false); }
  };

  const confirm = async (cid) => {
    if (!window.confirm("Confirmer la reconduction ?")) return;
    try {
      await api.post(`/contracts/${cid}/confirm-renewal`);
      toast.success("Reconduction confirmée.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (cid) => {
    if (!window.confirm("Rejeter la reconduction ? Le contrat sera marqué expiré.")) return;
    try {
      await api.post(`/contracts/${cid}/reject-renewal`);
      toast.success("Reconduction rejetée.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="client-renewals-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            <RefreshCw className="h-7 w-7 text-blue-600" /> Reconductions clients
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Cycle tacite des contrats vendus aux clients — propositions auto,
            confirmations, historique.
          </p>
        </div>
        {canWrite && (
          <Button onClick={scan} disabled={scanning}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="scan-auto-propose-btn">
            <Zap className="h-4 w-4 mr-1.5" />
            {scanning ? "Scan en cours…" : "Scan auto-proposal"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-amber-600">À confirmer</div>
          <div className="mt-1 text-2xl font-semibold font-mono">{toConfirm.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-600">En reconduction</div>
          <div className="mt-1 text-2xl font-semibold font-mono">{inRenewal.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Échéance ≤ 90j</div>
          <div className="mt-1 text-2xl font-semibold font-mono">{upcoming.length}</div>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="to-confirm">
        <TabsList>
          <TabsTrigger value="to-confirm" data-testid="tab-to-confirm">
            À confirmer ({toConfirm.length})
          </TabsTrigger>
          <TabsTrigger value="in-renewal" data-testid="tab-in-renewal">
            En reconduction ({inRenewal.length})
          </TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">
            Échéances ({upcoming.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="to-confirm">
          <RenewalsTable rows={toConfirm} loading={loading}
                          renderActions={canWrite ? (c) => (
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="outline" onClick={() => confirm(c.id)}
                                       className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                       data-testid={`confirm-renewal-${c.id}`}>
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirmer
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => reject(c.id)}
                                       className="border-red-200 text-red-600 hover:bg-red-50"
                                       data-testid={`reject-renewal-${c.id}`}>
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Rejeter
                              </Button>
                            </div>
                          ) : null}
                          emptyLabel="Aucune reconduction à confirmer." />
        </TabsContent>
        <TabsContent value="in-renewal">
          <RenewalsTable rows={inRenewal} loading={loading}
                          emptyLabel="Aucun contrat en reconduction active." />
        </TabsContent>
        <TabsContent value="upcoming">
          <RenewalsTable rows={upcoming} loading={loading}
                          emptyLabel="Aucune échéance dans les 90 prochains jours." />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RenewalsTable({ rows, loading, renderActions, emptyLabel }) {
  if (loading) {
    return <Card className="p-8 text-sm text-slate-500 text-center">Chargement…</Card>;
  }
  if (!rows.length) {
    return (
      <Card className="p-8 text-center border-dashed border-slate-300 bg-white"
              data-testid="empty-state">
        <Clock className="h-8 w-8 mx-auto text-slate-300" />
        <div className="mt-3 text-sm text-slate-500">{emptyLabel}</div>
      </Card>
    );
  }
  return (
    <Card className="border-slate-200 rounded-xl bg-white overflow-hidden">
      <Table>
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead>Contrat</TableHead>
          <TableHead>Client</TableHead>
          <TableHead>Fin</TableHead>
          <TableHead>Jours</TableHead>
          <TableHead className="text-right">MRR</TableHead>
          <TableHead>État</TableHead>
          {renderActions && <TableHead className="text-right">Actions</TableHead>}
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((c) => (
            <TableRow key={c.id} data-testid={`renewal-row-${c.id}`}
                        className="hover:bg-slate-50">
              <TableCell>
                <Link to={`/app/contracts/${c.id}`}
                       className="font-medium hover:underline">
                  {c.title}
                </Link>
                {c.reference && (
                  <span className="ml-2 text-[10px] font-mono text-slate-400">
                    {c.reference}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-xs">{c.party_name}</TableCell>
              <TableCell className="font-mono text-xs">{fmtDate(c.end_date)}</TableCell>
              <TableCell>
                <Badge className={
                  c.days_until_end == null ? "bg-slate-100"
                  : c.days_until_end < 0 ? "bg-red-100 text-red-700"
                  : c.days_until_end <= 30 ? "bg-amber-100 text-amber-700"
                  : "bg-slate-100 text-slate-600"}>
                  {c.days_until_end == null ? "—"
                    : c.days_until_end < 0 ? `J+${Math.abs(c.days_until_end)}`
                    : `J-${c.days_until_end}`}
                </Badge>
              </TableCell>
              <TableCell className="text-right font-mono">
                {fmtEUR(c.amount_ht || 0)}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-[10px]">
                  {c.reconduction_state || "—"}
                </Badge>
              </TableCell>
              {renderActions && <TableCell className="text-right">{renderActions(c)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
